import {
  faFileAlt,
  faFolder,
  faSearch,
  faUser,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import useNotification from 'hooks/useNotification';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  FormControl,
  InputGroup,
  Spinner
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import KbArticlesService from 'services/KbArticles.service';



const ArticleSearchResults = () => {
  const { showErrorNotification } = useNotification();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchContainerRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const MIN_SEARCH_LENGTH = 3;

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Função de busca para a preview
  const searchArticles = async term => {
    if (!term.trim() || term.trim().length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await KbArticlesService.search(term);

      if (response.userMessage) {
        showErrorNotification(response.userMessage);
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      setSearchResults(response);
      setShowResults(true);
    } catch (err) {
      setError('Erro ao buscar artigos. Tente novamente.');
      console.error('Erro na busca:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(debounce(searchArticles, 300), []);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = value => {
    setSearchTerm(value);
    if (value.trim().length >= MIN_SEARCH_LENGTH && searchResults.length > 0) {
      setShowResults(true);
    }
  };

  const handleInputFocus = () => {
    if (
      searchTerm.trim().length >= MIN_SEARCH_LENGTH &&
      searchResults.length > 0
    ) {
      setShowResults(true);
    }
  };

  const handleSearchSubmit = () => {
    if (searchTerm.trim().length >= MIN_SEARCH_LENGTH) {
      const encodedTerm = encodeURIComponent(searchTerm.trim());
      navigate(`/documents/article-search?searchTerm=${encodedTerm}`);
      setShowResults(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleArticleClick = article => {
    navigate(`/documents/articles/${article.id}`);
    setShowResults(false);
  };

  const getStatusBadgeVariant = status => {
    switch (status) {
      case 'PUBLISHED': return 'success';
      case 'DRAFT': return 'warning';
      default: return 'secondary';
    }
  };

  const getVisibilityIcon = visibleTo => {
    switch (visibleTo) {
      case 'ALL_AGENTS':
      case 'ALL_CUSTOMERS': return faUsers;
      case 'AGENT':
      case 'CUSTOMER': return faUser;
      default: return faFolder;
    }
  };

  const getVisibilityText = visibleTo => {
    switch (visibleTo) {
      case 'ALL_AGENTS': return 'Todos os agentes';
      case 'ALL_CUSTOMERS': return 'Todos os clientes';
      case 'AGENT': return 'Agentes específicos';
      case 'CUSTOMER': return 'Clientes específicos';
      default: return visibleTo;
    }
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div
      className="article-search-container"
      ref={searchContainerRef}
      style={{ position: 'relative' }}
    >
      <div className="mb-3">
        <InputGroup>
          <FormControl
            ref={inputRef}
            size="sm"
            type="search"
            placeholder={`Pesquisar artigos (mín. ${MIN_SEARCH_LENGTH} caracteres)`}
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            style={{ borderRadius: '13px 0px 0px 13px' }}
          />
          <Button
            size="sm"
            variant="outline-secondary"
            className="border-300 hover-border-secondary"
            style={{ borderRadius: '0px 13px 13px 0px' }}
            disabled={isLoading}
            onClick={handleSearchSubmit}
          >
            {isLoading ? (
              <Spinner as="span" animation="border" size="sm" role="status" />
            ) : (
              <FontAwesomeIcon icon={faSearch} className="fs-10" />
            )}
          </Button>
        </InputGroup>
      </div>

      {showResults && (
        <div
          className="search-results-dropdown bg-100"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1050,
            borderRadius: '0.375rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
            maxHeight: '400px',
            overflowY: 'auto'
          }}
        >
          {error && (
            <div className="p-3">
              <Alert variant="danger" className="mb-0">
                {error}
              </Alert>
            </div>
          )}

          {searchResults.length === 0 &&
            !isLoading &&
            searchTerm.trim().length >= MIN_SEARCH_LENGTH && (
              <Flex
                className="p-3 text-muted"
                justifyContent={'center'}
                alignItems={'center'}
              >
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                Nenhum artigo encontrado para "{searchTerm}"
              </Flex>
            )}

          {searchTerm.trim().length > 0 &&
            searchTerm.trim().length < MIN_SEARCH_LENGTH && (
              <div className="p-3 text-muted small">
                Digite pelo menos {MIN_SEARCH_LENGTH} caracteres para pesquisar
              </div>
            )}

          {searchResults.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 bg-100 border-bottom">
                <small className="text-muted">
                  {searchResults.length} resultado
                  {searchResults.length !== 1 ? 's' : ''} encontrado
                  {searchResults.length !== 1 ? 's' : ''}
                </small>
              </div>

              <div className="search-results-list">
                {searchResults.map((article, index) => (
                  <div
                    key={article.id}
                    role="button"
                    className="search-result-item p-3 border-bottom"
                    style={{ transition: 'background-color 0.2s' }}
                    onClick={() => handleArticleClick(article)}
                  >
                    <Flex justifyContent={'between'} alignItems={'start'}>
                      <div className="flex-grow-1">
                        <Flex alignItems={'center'} className="mb-1">
                          <FontAwesomeIcon
                            icon={faFileAlt}
                            className="me-2 text-muted"
                            size="sm"
                          />
                          <Link
                            to={`/documents/articles/${article.id}`}
                            className="mb-0 text-primary fw-semibold fs-10"
                          >
                            {article.title}
                          </Link>
                        </Flex>

                        <div className="text-muted small mb-1">
                          <Flex alignItems={'center'}>
                            <FontAwesomeIcon
                              icon={faFolder}
                              className="me-1"
                              size="sm"
                            />
                            <strong>{article.kbFolder.kbGroup.name}</strong>
                            <span className="mx-1">›</span>
                            <span>{article.kbFolder.name}</span>
                          </Flex>
                        </div>

                        <Flex alignItems={'center'} className="text-muted small">
                          <FontAwesomeIcon
                            icon={getVisibilityIcon(article.kbFolder.visibleTo)}
                            className="me-1"
                            size="sm"
                          />
                          <span className="me-3">
                            {getVisibilityText(article.kbFolder.visibleTo)}
                          </span>
                          <span>
                            Atualizado: {formatDate(article.lastUpdateDate)}
                          </span>
                        </Flex>
                      </div>

                      <div className="ms-3">
                        <Badge
                          bg={getStatusBadgeVariant(article.status.status)}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {article.status.description}
                        </Badge>
                      </div>
                    </Flex>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ArticleSearchResults;
